/**
 * 存储服务
 * 封装 localforage，并按业务域拆分为独立存储空间：
 * - map: 地图缓存
 * - save: 存档与备份
 * - settings: 全局设置与 LLM 配置
 */

import { LogService } from '@/IsekaiRPG/shared/logging/logService';
import localforage from 'localforage';

/**
 * 存储命名空间
 */
export type StorageNamespace = 'map' | 'save' | 'settings';

export interface GetItemOptions {
  onVersionMismatch?: 'remove' | 'keep';
}

const DB_NAME = 'IsekaiRPG';
const DB_VERSION = 1.0;
type LocalForageInstance = ReturnType<typeof localforage.createInstance>;

// 各命名空间实例
const storages: Record<StorageNamespace, LocalForageInstance> = {
  map: localforage.createInstance({
    name: DB_NAME,
    storeName: 'map_data',
    description: 'Storage for IsekaiRPG map cache',
    version: DB_VERSION,
  }),
  save: localforage.createInstance({
    name: DB_NAME,
    storeName: 'save_data',
    description: 'Storage for IsekaiRPG save data',
    version: DB_VERSION,
  }),
  settings: localforage.createInstance({
    name: DB_NAME,
    storeName: 'settings_data',
    description: 'Storage for IsekaiRPG settings data',
    version: DB_VERSION,
  }),
};

function getStorage(namespace: StorageNamespace): LocalForageInstance {
  return storages[namespace];
}

/**
 * 存储项接口
 */
interface StorageItem<T> {
  version: string; // 数据版本号
  timestamp: number; // 存储时间戳
  data: T; // 实际数据
}

/**
 * 存储服务类
 */
export class StorageService {
  /**
   * 获取数据
   * @param key 键名
   * @param version 版本号（可选，如果提供则会检查版本）
   * @param namespace 存储域（默认 map）
   * @returns 数据或 null
   */
  static async getItem<T>(
    key: string,
    version?: string,
    namespace: StorageNamespace = 'map',
    options: GetItemOptions = {},
  ): Promise<T | null> {
    const storage = getStorage(namespace);
    try {
      const item = await storage.getItem<StorageItem<T>>(key);

      if (!item) return null;

      // 如果指定了版本号，检查版本是否匹配
      if (version && item.version !== version) {
        LogService.info('StorageService', `${key} 版本不匹配。预期: ${version}, 实际: ${item.version}`);
        if (options.onVersionMismatch !== 'keep') {
          // 版本不匹配时默认删除旧数据，供纯缓存场景自动清理
          await this.removeItem(key, namespace);
        }
        return null;
      }

      return item.data;
    } catch (error) {
      LogService.error('StorageService', `获取项目 ${key} 失败 (namespace=${namespace})`, error as Error);
      return null;
    }
  }

  /**
   * 存储数据
   * @param key 键名
   * @param data 数据
   * @param version 版本号（默认为 '1.0.0'）
   * @param namespace 存储域（默认 map）
   */
  static async setItem<T>(
    key: string,
    data: T,
    version: string = '1.0.0',
    namespace: StorageNamespace = 'map',
  ): Promise<boolean> {
    const storage = getStorage(namespace);
    try {
      const item: StorageItem<T> = {
        version,
        timestamp: Date.now(),
        data,
      };

      await storage.setItem(key, item);
      LogService.info('StorageService', `已保存 ${key} (v${version}) 到存储 (namespace=${namespace})`);
      return true;
    } catch (error) {
      LogService.error('StorageService', `保存项目 ${key} 失败 (namespace=${namespace})`, error as Error);
      // 存储失败不应中断流程，只是无法缓存
      return false;
    }
  }

  /**
   * 移除数据
   * @param key 键名
   * @param namespace 存储域（默认 map）
   */
  static async removeItem(key: string, namespace: StorageNamespace = 'map'): Promise<void> {
    const storage = getStorage(namespace);
    try {
      await storage.removeItem(key);
      LogService.info('StorageService', `已从存储中移除 ${key} (namespace=${namespace})`);
    } catch (error) {
      LogService.error('StorageService', `移除项目 ${key} 失败 (namespace=${namespace})`, error as Error);
    }
  }

  /**
   * 清除所有数据
   * @param namespace 存储域（默认 map）
   */
  static async clear(namespace: StorageNamespace = 'map'): Promise<void> {
    const storage = getStorage(namespace);
    try {
      await storage.clear();
      LogService.info('StorageService', `已清空存储 (namespace=${namespace})`);
    } catch (error) {
      LogService.error('StorageService', `清空存储失败 (namespace=${namespace})`, error as Error);
    }
  }

  /**
   * 获取存储中的键列表
   * @param namespace 存储域（默认 map）
   */
  static async keys(namespace: StorageNamespace = 'map'): Promise<string[]> {
    const storage = getStorage(namespace);
    try {
      return await storage.keys();
    } catch (error) {
      LogService.error('StorageService', `获取所有键名失败 (namespace=${namespace})`, error as Error);
      return [];
    }
  }
}
